/* eslint-disable no-nested-ternary */
import { CSSObjectWithLabel, ControlProps, OptionProps, StylesConfig } from 'react-select'
import { DefaultTheme } from 'styled-components'

import { CustomSelectInputStyle } from './types'

interface ISelectInputArgs {
  theme: DefaultTheme
  requiredMissingField?: boolean
  error?: boolean
  customSelectInputStyle?: CustomSelectInputStyle
}

export const getListingFlowAddressSelectStyles = ({
  colors,
}: {
  colors: DefaultTheme['colors']
}): StylesConfig<any, boolean> => ({
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    '&:hover': {
      borderColor: colors.white,
    },
    'background': colors.blue12,
    'borderColor': colors.white,
    'borderWidth': 1,
    'cursor': 'pointer',
    'fontSize': 14,
    'height': 32,
    'padding': '6px 20px',
  }),
  option: (provided: CSSObjectWithLabel, state: OptionProps<any, boolean>) => ({
    ...provided,
    'fontSize': 14,
    'fontWeight': 500,
    'display': 'flex',
    'alignItems': 'center',
    'color': state.isSelected ? colors.blue4 : colors.black,
    'background': state.isSelected ? colors.blue13 : colors.white,
    'cursor': state.isDisabled ? 'not-allowed' : 'pointer',
    'padding': '4px 8px',
    'margin': '5px 0',
    '&:hover': {
      color: colors.blue4,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 100,
  }),
  menuList: (provided: CSSObjectWithLabel) => ({
    ...provided,
    borderRadius: 4,
    boxShadow: `14px 8px 24px ${colors.transparentGrey23Alpha25}`,
    padding: '0 10px',
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: colors.white,
    fontWeight: 800,
  }),
})

export const getFormBuilderSelectInputStyles = ({
  theme: { colors },
  requiredMissingField,
  error,
  customSelectInputStyle = {},
}: ISelectInputArgs): StylesConfig<any, boolean> => ({
  control: (provided: CSSObjectWithLabel, state: ControlProps<any, boolean>) => ({
    ...provided,
    '&:active, &:focus': {
      borderColor: colors.blue,
    },
    'backgroundColor':
      requiredMissingField || error ? colors.red4 : colors.transparentGrey30Alpha50,
    'borderColor':
      requiredMissingField || error ? colors.red : state.isFocused ? colors.blue : colors.grey30,
    'borderWidth': '1px',
    'boxShadow': requiredMissingField || error ? '0px 0px 4px rgba(231, 58, 62, 0.5)' : 'none',
    'cursor': 'pointer',
    'fontSize': 16,
    'padding': '5px 2px',
    'height': 36,
    'minHeight': 36,
    '&:hover': {
      borderColor: state.isFocused
        ? colors.blue
        : requiredMissingField
          ? colors.red
          : colors.grey30,
    },
    ...(customSelectInputStyle.control || {}),
  }),
  option: (provided: CSSObjectWithLabel, state: OptionProps<any, boolean>) => ({
    ...provided,
    'fontSize': 14,
    'fontWeight': 500,
    'display': 'flex',
    'alignItems': 'center',
    'color': state.isSelected ? colors.blue4 : colors.black,
    'background': state.isSelected ? colors.blue13 : colors.white,
    'cursor': state.isDisabled ? 'not-allowed' : 'pointer',
    'padding': '5px 8px',
    'margin': '0',
    'lineHeight': '22px',
    '&:hover, &:focus, &:active': {
      color: colors.blue4,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    boxShadow: 'none',
    zIndex: 100,
  }),
  menuList: (provided: CSSObjectWithLabel) => ({
    ...provided,
    borderRadius: 4,
    boxShadow: `0px 6px 15px ${colors.transparentBlackAlpha20}`,
    padding: '4px',
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: 0,
    ...(customSelectInputStyle.singleValue || {}),
  }),
})

export const getCustomStyles = (
  { colors }: any,
  custom: boolean | undefined,
  requiredMissingField?: boolean,
  backgroundColor?: string,
  error?: boolean,
  customSelectInputStyle: CustomSelectInputStyle = {},
): StylesConfig<any, boolean> => {
  const mainColor: string = custom ? colors.purple : colors.blue
  const darkColor: string = custom ? colors.purple2 : colors.blue3
  const lightColor = custom ? colors.transparentPurpleAlpha20 : colors.blue2
  return {
    control: (provided: CSSObjectWithLabel, state: ControlProps<any, boolean>) => ({
      ...provided,
      'minHeight': custom ? 42 : 36,
      'background': error
        ? colors.transparentRedAlpha10
        : backgroundColor || (custom ? colors.white : colors.transparentGrey30Alpha50),
      'borderColor':
        requiredMissingField || error ? colors.red : state.isFocused ? mainColor : colors.grey30,
      'borderWidth': requiredMissingField || error ? '2px' : '1px',
      'boxShadow': state.isFocused
        ? custom
          ? `box-shadow: 0px 0px 4px rgba(0, 101, 255, 0.24), inset 0px 0px 4px ${colors.transparentBlue14Alpha50}`
          : `0 0 0 1px ${colors.blue}`
        : '',
      '&:hover': {
        borderColor: state.isFocused ? mainColor : requiredMissingField ? colors.red : colors.grey9,
      },
      'cursor': 'pointer',
    }),
    option: (provided: CSSObjectWithLabel, state: OptionProps<any, boolean>) => ({
      ...provided,
      fontSize: state.isDisabled ? 14 : 16,
      fontWeight: state.isSelected ? 900 : 500,
      display: 'flex',
      alignItems: 'center',
      // height: 44,
      paddingTop: 16, // 4px font correction + 12px vertical padding
      paddingBottom: 12,
      paddingLeft: 20,
      paddingRight: 20,
      color: state.isDisabled ? colors.red : state.isSelected ? colors.white : colors.black,
      background: state.isSelected ? darkColor : state.isFocused ? lightColor : colors.white,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      fontStyle: state.isDisabled ? 'italic' : 'auto',
    }),
    placeholder: (provided: CSSObjectWithLabel) => ({
      ...provided,
      fontSize: 16,
      color: colors.grey27,
      paddingTop: 4,
      ...(customSelectInputStyle.placeholder || {}),
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided: CSSObjectWithLabel) => ({
      ...provided,
      boxShadow: `0 4px 11px ${colors.transparentBlackAlpha10}`,
      border: `1px solid ${colors.grey}`,
      zIndex: 100,
    }),
    menuList: (provided: CSSObjectWithLabel) => ({
      ...provided,
      padding: 0,
      borderRadius: 4,
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
      ...provided,
      paddingTop: 4,
      color: colors.grey27,
      ...(customSelectInputStyle.singleValue || {}),
    }),
  }
}
